// src/screens/MyWallet.js

import React, { useEffect, useState } from 'react';
import './MyWallet.css';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import backIcon from '../assets/backspace-icon.png';
import TopUpDrawer from '../components/popups/TopUpDrawer';
import WithdrawDrawer from '../components/popups/WithdrawDrawer';
import TopUpErrorDrawer from '../components/popups/TopUpErrorDrawer';
import { setupPiMessageBridge } from '../utils/piBridge';
import { startPiTopUp } from '../utils/piTopUp';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                         User-friendly wallet history                        */
/* -------------------------------------------------------------------------- */

const formatPiAmount = (amount) => {
  const num = Number(amount || 0);

  if (num === 0) return 'Completed';

  const abs = Math.abs(num);
  const clean = Number.isInteger(abs) ? abs.toString() : abs.toFixed(2).replace(/\.?0+$/, '');

  return `${num > 0 ? '+' : '-'}${clean} Pi`;
};

const extractStyleName = (description = '') => {
  // Example backend desc: "Create Post background purchase: Gold Soft"
  if (!description) return '';
  const parts = description.split(':');
  return parts.length > 1 ? parts[parts.length - 1].trim() : '';
};

const extractUsername = (description = '') => {
  const match = description.match(/@\w[\w._]*/);
  return match ? match[0] : '';
};


const extractWithdrawalAddress = (description = '') => {
  if (!description) return '';

  /*
   * Matches the destination address from descriptions such as:
   *
   * "Requested mainnet withdrawal to GABC..."
   * "Withdrawal of 20 Pi approved and sent to GABC... (mainnet)"
   */
  const match = description.match(
    /(?:sent to|withdrawal to|to)\s+([A-Za-z0-9]{20,})/i
  );

  return match ? match[1] : '';
};


const getFriendlyWalletTx = (tx = {}) => {
  const type = tx.type || '';
  const amount = Number(tx.amount || 0);
  const description = tx.description || '';

  switch (type) {
    case 'deposit':
      return {
        title: 'Top Up',
        description: 'Pi was added to your BarterFi wallet.',
      };



case 'withdrawal':
case 'withdrawal_request': {
  const walletAddress = extractWithdrawalAddress(description);

  return {
    title: 'Withdrawal Requested',
    description: walletAddress
      ? `Your Pi withdrawal request was submitted to ${walletAddress}.`
      : 'Your Pi withdrawal request was submitted.',
  };
}

case 'withdrawal_complete': {
  const walletAddress = extractWithdrawalAddress(description);

  return {
    title: 'Withdrawal Completed',
    description: walletAddress
      ? `Your Pi withdrawal was completed and sent to ${walletAddress}.`
      : 'Your Pi withdrawal was completed.',
  };
}

case 'withdrawal_refund':
  return {
    title: 'Withdrawal Rejected',
    description:
      'Your withdrawal was rejected and the Pi was returned to your wallet.',
  };


    case 'tip': {
      const username = extractUsername(description);
      return {
        title: 'Tip Sent',
        description: username ? `You sent a tip to ${username}.` : 'You sent a tip.',
      };
    }

    case 'tip_received': {
      const username = extractUsername(description);
      return {
        title: 'Tip Received',
        description: username ? `You received a tip from ${username}.` : 'You received a tip.',
      };
    }

    case 'escrow_hold':
      return {
        title: 'Escrow Started',
        description: 'Pi was held safely for a marketplace order.',
      };

    case 'escrow_release':
      return {
        title: 'Escrow Released',
        description: 'Escrow was released for the order.',
      };

    case 'escrow_payout':
    case 'escrow_received':
      return {
        title: 'Marketplace Payment',
        description: 'You received Pi from a marketplace order.',
      };

    case 'escrow_refund':
      return {
        title: 'Escrow Refund',
        description: 'Pi was returned to your wallet.',
      };

    case 'sponsorship_payment':
      return {
        title: 'Product Promotion',
        description: 'Your product promotion was activated.',
      };

    case 'subscription_payment':
      return {
        title: 'Plan Payment',
        description: 'Your BarterFi plan payment was successful.',
      };

    case 'post_stake_hold':
      return {
        title: 'Post Boost Started',
        description: 'Your post was boosted.',
      };

    case 'post_stake_refund':
      return {
        title: 'Post Boost Returned',
        description: 'Your boost ended and Pi was returned to your wallet.',
      };

    case 'post_background_purchase': {
      const styleName = extractStyleName(description);
      return {
        title: 'Post Style Purchase',
        description: styleName
          ? `${styleName} style was applied to your post.`
          : 'A post style was applied to your post.',
      };
    }

    case 'post_background_refund':
      return {
        title: 'Post Style Refund',
        description: 'Your post style payment was returned to your wallet.',
      };

    default:
      return {
        title: type ? type.replaceAll('_', ' ') : 'Wallet Activity',
        description:
          amount > 0
            ? 'Pi was added to your wallet.'
            : amount < 0
              ? 'Pi was used from your wallet.'
              : 'Wallet activity completed.',
      };
  }
};

const formatWalletDate = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

function MyWallet({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [balance, setBalance] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showWithdrawDrawer, setShowWithdrawDrawer] = useState(false);
  const [history, setHistory] = useState([]);
  const [successBanner, setSuccessBanner] = useState('');
  const [isErrorBanner, setIsErrorBanner] = useState(false);
  const [walletInactive, setWalletInactive] = useState(false);
  const [showTopUpError, setShowTopUpError] = useState(false);
  const [topUpErrorMessage, setTopUpErrorMessage] = useState('');
  const [networkLabel, setNetworkLabel] = useState('');

  // Small on-screen diagnostics
  const [piPresent, setPiPresent] = useState(false);
  const [piMode, setPiMode] = useState('sandbox');

  // ========= host/network helpers =========
  const host = window.location.hostname;
  const isTestnetHost = host.includes('testnet.');
  const isMainnetHost = !isTestnetHost && host.includes('barterfi.app');

  // SDK sandbox flag purely from host
  const isSandbox = isTestnetHost;

  // App ids per network (must match Pi Dev Console)
  const APP_ID_TESTNET = process.env.REACT_APP_PI_APP_ID_TESTNET;
  const APP_ID_MAINNET = process.env.REACT_APP_PI_APP_ID_MAINNET;

  // Activation threshold from env (frontend)

const MIN_MAINNET_ACTIVATION_PI = Number(
  process.env.REACT_APP_MIN_MAINNET_ACTIVATION_PI || 0.1
);

  // Minimum withdrawal (frontend guard; backend also enforces)
  const MIN_WITHDRAW_PI = Number(process.env.REACT_APP_MIN_WITHDRAW_PI || 10);

  // Minimum top-up (frontend guard; backend also enforces)
  const MIN_TOPUP_PI = Number(process.env.REACT_APP_MIN_TOPUP_PI || 0.1);

  // Inspect incoming Pi SDK messages (debug)
  const handlePiSdkMessage = (msg /* , event */) => {
    try {
      if (typeof msg === 'string') msg = JSON.parse(msg);
    } catch (_) {
      // ignore
    }

    if (msg?.type?.startsWith?.('@pi:')) {
      console.log('[PiSDK]', msg.type, msg.payload || {});
    }
  };

  useEffect(() => {
    if (successBanner) {
      const timer = setTimeout(() => setSuccessBanner(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [successBanner]);

  // ✅ Robust Pi SDK initialization with fallback loader + per-host appId
  useEffect(() => {
    let cancelled = false;

    const loadPiSdkIfNeeded = () =>
      new Promise((resolve) => {
        if (window.Pi && window.Pi.init) return resolve();

        const script = document.createElement('script');
        script.src = 'https://sdk.minepi.com/pi-sdk.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = resolve;
        document.head.appendChild(script);
      });

    (async () => {
      try {
        await loadPiSdkIfNeeded();
        if (cancelled) return;

        if (!window.Pi || !window.Pi.init) {
          console.warn('Pi SDK still not available after load attempt');
          setPiPresent(false);
          return;
        }

        const appId = isSandbox ? APP_ID_TESTNET : APP_ID_MAINNET;
        window.Pi.init({ version: '2.0', sandbox: isSandbox, appId });

        console.log(
          `✅ Pi SDK initialized in ${isSandbox ? 'SANDBOX (testnet)' : 'MAINNET'} mode`,
          { appId }
        );

        setPiPresent(true);
        setPiMode(isSandbox ? 'sandbox' : 'mainnet');
      } catch (err) {
        console.error('❌ Failed to initialize Pi SDK:', err);
        setPiPresent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSandbox, APP_ID_TESTNET, APP_ID_MAINNET]);

  // 🧩 Bridge: accept and route messages from SDK
  useEffect(() => {
    const cleanup = setupPiMessageBridge((event) => {
      let msg = event.data;
      handlePiSdkMessage(msg, event);
    });

    return cleanup;
  }, [isSandbox]);

  // ✅ Set label immediately based on host
  useEffect(() => {
    setNetworkLabel(isTestnetHost ? t('network.testnet') : t('network.mainnet'));
  }, [isTestnetHost, t]);

  const fetchWallet = async () => {
    try {
      const res = await api.get(`/wallet/${user.username}`);
      console.log('🟢 Wallet fetched:', res.data);

      setBalance(res.data.balance ?? 0);
      setHistory(res.data.history ?? []);

      // Always derive label from host so UI is correct
      setNetworkLabel(isTestnetHost ? t('network.testnet') : t('network.mainnet'));

      // Activation logic (UI only) — only show on MAINNET view
      if (isMainnetHost) {
        const depositTotal = (res.data.history || [])
          .filter((tx) => ['deposit', 'topup'].includes(tx.type))
          .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

        const inactive = depositTotal < MIN_MAINNET_ACTIVATION_PI;
        setWalletInactive(inactive);

        console.log('🔐 UI activation check:', {
          depositTotal,
          threshold: MIN_MAINNET_ACTIVATION_PI,
          inactive,
        });
      } else {
        setWalletInactive(false);
      }
    } catch (err) {
      console.error('❌ Failed to fetch wallet:', err);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, [user.username, isTestnetHost, t]);

  const handleLogout = () => {
    console.log('🚪 Logout from MyWallet button triggered');
    onLogout();
  };

  // 💳 Top Up (shared Pi top-up util)
  const handleTopUp = async (amount) => {
    console.log('🟡 Drawer submitting top-up amount:', amount);

    const result = await startPiTopUp({
      amount,
      username: user.username,
      t,
      onWalletRefresh: fetchWallet,
      onRecovered: (recovered) => {
        setIsErrorBanner(false);
        setSuccessBanner(recovered.message || t('topup.recovered'));
      },
      onSuccess: (success) => {
        setIsErrorBanner(false);
        setSuccessBanner(success.message || t('topup.success', { amount: Number(amount) }));
        setShowTopUp(false);
      },
      onCancel: (cancelled) => {
        setIsErrorBanner(true);
        setTopUpErrorMessage(cancelled.message || t('topup.errors.cancelled'));
        setShowTopUpError(true);
      },
      onError: (error) => {
        setIsErrorBanner(true);
        setTopUpErrorMessage(
          error.message || t('topup.errors.generic', { msg: 'Unknown error' })
        );
        setShowTopUpError(true);
      },
    });

    return result;
  };

  // 📤 Withdraw (request only — manual payout later)
  const handleWithdraw = async ({ amount, walletAddress }) => {
    console.log('📤 Submitting withdrawal request...');
    console.log('💰 Requested amount:', amount);
    console.log('📬 Destination wallet address:', walletAddress);

    if (!amount || isNaN(amount) || amount <= 0) {
      setIsErrorBanner(true);
      setSuccessBanner(t('withdraw.errors.invalid_amount'));
      return;
    }

    if (Number(amount) < MIN_WITHDRAW_PI) {
      setIsErrorBanner(true);
      setSuccessBanner(t('withdraw.errors.min', { min: MIN_WITHDRAW_PI }));
      return;
    }

    if (amount > balance) {
      setIsErrorBanner(true);
      setSuccessBanner(t('withdraw.errors.not_enough'));
      return;
    }

    try {
      const res = await api.post('/withdrawals/request', {
        username: user.username,
        amount,
        walletAddress,
      });

      console.log('Withdrawal requested successfully:', res.data);
      setIsErrorBanner(false);
      setSuccessBanner(t('withdraw.success', { amount }));
      await fetchWallet();
    } catch (err) {
      console.error('❌ Withdrawal request failed:', err);
      const code = err?.response?.data?.error || err?.response?.data?.code;

      if (code === 'PENDING_WITHDRAWAL_EXISTS') {
        setIsErrorBanner(true);
        setSuccessBanner(t('withdraw.errors.pending_exists'));
      } else {
        setIsErrorBanner(true);
        setSuccessBanner(t('withdraw.errors.submit_failed'));
      }
    }
  };

  return (
    <div className="mywallet-container">
      <div className="mywallet-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="mywallet-back-icon"
          onClick={() => navigate(-1)}
        />

        <h2>{t('wallet.title')}</h2>

        <button className="logout-btn-top" onClick={handleLogout}>
          {t('auth.logout')}
        </button>
      </div>

      {walletInactive && isMainnetHost && (
        <div className="wallet-activation-warning">
          <p>{t('wallet.activate_banner', { amount: MIN_MAINNET_ACTIVATION_PI })}</p>
        </div>
      )}

      {successBanner && (
        <div className={`wallet-success-banner ${isErrorBanner ? 'error' : ''}`}>
          {successBanner}
        </div>
      )}

      <div className="mywallet-body">
        <div className="wallet-balance-card">
          <p className="wallet-balance-title">{t('wallet.available_amount')}</p>
          <p className="wallet-testnet-label">{networkLabel || '—'}</p>

          <h1 className="wallet-balance">
            {typeof balance === 'number' ? balance.toFixed(2) : '—'} Pi
          </h1>
        </div>

        <div className="wallet-history-header">
          <h3>{t('wallet.history')}</h3>
          <p>Your recent wallet activity</p>
        </div>

        {history.length === 0 ? (
          <div className="wallet-history-placeholder">
            <p>{t('wallet.no_tx')}</p>
          </div>
        ) : (
          <div className="wallet-history-list">
            {[...history]
              .sort(
                (a, b) =>
                  new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)
              )
              .map((tx, index) => {
                const friendlyTx = getFriendlyWalletTx(tx);
                const amount = Number(tx.amount || 0);
                const dateText = formatWalletDate(tx.createdAt || tx.date);

                return (
                  <div className="wallet-history-row-stacked" key={`${tx.type}-${index}`}>
                    <div className="tx-top-row">
                      <div className="tx-type">{friendlyTx.title}</div>

                      <div
                        className={`tx-amount ${
                          amount > 0 ? 'green' : amount < 0 ? 'red' : 'neutral'
                        }`}
                      >
                        {formatPiAmount(amount)}
                      </div>
                    </div>

                    <div className="tx-description">{friendlyTx.description}</div>

                    {dateText && <div className="tx-date">{dateText}</div>}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <div className="wallet-footer-buttons">
        <button className="wallet-topup-btn" onClick={() => setShowTopUp(true)}>
          {t('wallet.top_up')}
        </button>

        <button
          className="wallet-withdraw-btn"
          onClick={() => setShowWithdrawDrawer(true)}
        >
          {t('wallet.withdraw')}
        </button>
      </div>

      {showTopUp && (
        <TopUpDrawer
          onClose={() => setShowTopUp(false)}
          onTopUp={handleTopUp}
          minAmount={MIN_TOPUP_PI}
        />
      )}

      {showWithdrawDrawer && (
        <WithdrawDrawer
          onClose={() => setShowWithdrawDrawer(false)}
          onWithdraw={handleWithdraw}
          minAmount={MIN_WITHDRAW_PI}
        />
      )}

      {showTopUpError && (
        <TopUpErrorDrawer
          onClose={() => setShowTopUpError(false)}
          message={topUpErrorMessage}
        />
      )}

      {isTestnetHost && (
        <div
          style={{
            position: 'fixed',
            bottom: 8,
            left: 8,
            fontSize: 12,
            background: '#f1f3f5',
            border: '1px solid #e1e5ea',
            borderRadius: 8,
            padding: '6px 8px',
            color: '#333',
            opacity: 0.9,
            zIndex: 9999,
          }}
        >
          Pi: {piPresent ? 'ready' : 'missing'} • {piMode}
        </div>
      )}
    </div>
  );
}

export default MyWallet;
